import { useState } from "react";

import { Plus } from "lucide-react";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Button } from "@/components/ui/button";
import { CTA_COLOR } from "@/css/constants";
import {
  FeatureIntroducedDocument,
  useIntroduceFeatureMutation,
} from "@/graphql";

const featureType = "PROGRAM_INTRO";

interface ProgramIntroComponentProps {
  onDismiss: () => void;
  onCreateProgram?: () => void;
}

function ProgramIntroComponent({
  onDismiss,
  onCreateProgram,
}: ProgramIntroComponentProps) {
  const isAdmin = isWorkspaceAdmin();

  return (
    <Flex
      flexWrap="wrap"
      style={{
        borderRadius: 6,
        borderLeft: `6px solid ${CTA_COLOR}`,
        gap: 30,
      }}
      className="self-stretch pl-7 pr-6 py-6 bg-blue-200 shadow-[0px_0px_5px_0px_rgba(0,0,0,0.05)] inline-flex justify-start items-center gap-10"
      alignItems="center"
      p={24}
    >
      <Box flex={[null, 1]}>
        <Box lineHeight="30px" fontSize="20px" fontWeight={600}>
          {isAdmin
            ? "Create and Manage Learning Programs"
            : "Learning Development Programs"}
        </Box>
        <Box mt={2} mb={24}>
          {isAdmin
            ? "Design custom learning experiences that combine AI roleplay scenarios, coaching sessions, videos, articles, and assessments to address your organization's specific development needs. From here, you can create new programs, edit existing ones, track participant engagement, and analyze program effectiveness across your workspace."
            : "Programs are structured learning experiences that combine AI roleplay practice, coaching sessions, videos, articles, and assessments to help you build specific skills. Your enrolled programs will appear here, where you can track your progress, access materials, and see your next steps all in one place."}
        </Box>
        <Flex>
          {isAdmin && onCreateProgram && (
            <Box mr={2}>
              <Button intent="primary" onClick={onCreateProgram}>
                Create Program <Plus />
              </Button>
            </Box>
          )}
          <Button
            className="border-none hover:bg-blue-950/10 text-center text-gray-700 text-base font-semibold font-['Inter'] leading-snug h-12 px-4 py-2.5 bg-blend-multiply bg-blue-950/5 inline-flex justify-center items-center gap-2.5"
            onClick={onDismiss}
          >
            Got It!
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}

interface ProgramIntroProps {
  show?: boolean;
  onCreateProgram?: () => void;
}

export function ProgramIntro({
  show = false,
  onCreateProgram,
}: ProgramIntroProps) {
  const [submitted, setSubmitted] = useState(false);
  const [introduceFeature] = useIntroduceFeatureMutation({
    variables: { featureType },
    refetchQueries: [
      { query: FeatureIntroducedDocument, variables: { featureType } },
    ],
  });

  if (submitted || !show) {
    return null;
  }

  function submit() {
    setSubmitted(true);
    introduceFeature();
  }

  return (
    <ProgramIntroComponent
      onDismiss={submit}
      onCreateProgram={onCreateProgram}
    />
  );
}
